/**
 * Wicket entry: one sheet, every field, no nesting.
 */
import { useState } from "react";
import type { BattingLine, DismissalType, PlayerId, RuleSet, Wicket } from "@sportsos/engine";
import { CATCH_DISMISSALS } from "@sportsos/engine";
import type { Latch } from "../match/entry.ts";
import {
  defaultsToNonStriker,
  dismissalLabel,
  dismissalOptions,
  likelyEnd,
  needsFielder,
} from "../match/entry.ts";
import type { SquadPlayer } from "../match/setup.ts";

export interface WicketResult {
  readonly wicket: Wicket;
  readonly runs: number;
}

interface Props {
  readonly latch: Latch;
  readonly freeHit: boolean;
  readonly ruleSet: RuleSet;
  readonly striker: BattingLine;
  readonly nonStriker: BattingLine;
  readonly bowlerId: PlayerId;
  readonly keeperId: PlayerId | null;
  readonly fielders: readonly SquadPlayer[];
  readonly recentFielderId: PlayerId | null;
  readonly initialRuns: number;
  readonly name: (playerId: PlayerId) => string;
  readonly onCancel: () => void;
  readonly onConfirm: (result: WicketResult) => void;
}

const isCatch = (type: DismissalType): boolean =>
  (CATCH_DISMISSALS as readonly DismissalType[]).includes(type);

export function WicketSheet({
  latch,
  freeHit,
  ruleSet,
  striker,
  nonStriker,
  bowlerId,
  keeperId,
  fielders,
  recentFielderId,
  initialRuns,
  name,
  onCancel,
  onConfirm,
}: Props): React.ReactElement {
  const [type, setType] = useState<DismissalType | null>(null);
  const [runs, setRuns] = useState(initialRuns);
  const [outOverride, setOutOverride] = useState<boolean | null>(null);
  const [fielderOverride, setFielderOverride] = useState<PlayerId | null>(null);
  const [endOverride, setEndOverride] = useState<Wicket["end"] | null>(null);
  const [crossed, setCrossed] = useState(false);

  const options = dismissalOptions(latch, freeHit, ruleSet);

  const chooseType = (next: DismissalType): void => {
    setType(next);
    setOutOverride(null);
    setFielderOverride(null);
    setEndOverride(null);
  };

  if (type === null) {
    return (
      <div style={{ padding: "var(--ds-space-3)", display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
        <div>
          <div className="ds-title">How was he out?</div>
          <div className="ds-caption">
            {latch.extra === null && !freeHit
              ? "Tap the dismissal"
              : "Greyed-out dismissals were not possible on this ball"}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--ds-space-2)", maxHeight: "40dvh", overflowY: "auto" }}>
          {options.map(({ type: option, blocked }) => (
            <button
              key={option}
              className={`ds-key ds-key--sm${blocked !== null ? "" : " ds-key--magenta"}`}
              disabled={blocked !== null}
              onClick={() => chooseType(option)}
              style={{
                height: 48,
                fontSize: "var(--ds-text-2xs)",
                fontFamily: "var(--ds-font-sans)",
                opacity: blocked !== null ? 0.35 : 1,
              }}
            >
              {dismissalLabel(option)}
            </button>
          ))}
        </div>
        <button className="ds-btn ds-btn--secondary ds-btn--block" onClick={onCancel}>Cancel</button>
      </div>
    );
  }

  const outIsStriker = outOverride ?? !defaultsToNonStriker(type);
  const playerOut = outIsStriker ? striker : nonStriker;

  const defaultFielder =
    type === "caught_behind" || type === "stumped"
      ? keeperId
      : (recentFielderId ?? keeperId);
  const fielderId = fielderOverride ?? defaultFielder;

  const end = endOverride ?? likelyEnd(outIsStriker, runs);
  const asksEnd = type === "run_out" || type === "mankad";
  const asksCrossed = isCatch(type) && !ruleSet.newBatterTakesStrikeOnCaught;

  const fielderIds: readonly PlayerId[] =
    type === "caught_and_bowled"
      ? [bowlerId]
      : needsFielder(type) && fielderId !== null
        ? [fielderId]
        : [];

  const missingFielder = needsFielder(type) && fielderIds.length === 0;

  const confirm = (): void => {
    onConfirm({
      runs,
      wicket: {
        type,
        playerOutId: playerOut.playerId,
        fielderIds,
        runsCompletedBefore: runs,
        end,
        crossed: asksCrossed ? crossed : runs > 0 ? runs % 2 === 1 : null,
      },
    });
  };

  return (
    <div style={{ padding: "var(--ds-space-3)", display: "flex", flexDirection: "column", gap: "var(--ds-space-3)" }}>
      {/* Header */}
      <div className="row">
        <div className="ds-title" style={{ flex: 1 }}>{dismissalLabel(type)}</div>
        <button className="ds-btn ds-btn--ghost ds-btn--sm" onClick={() => setType(null)}>
          change
        </button>
      </div>

      {/* Who is out */}
      <div>
        <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-2)" }}>Who is out</span>
        <div className="ds-options">
          {[
            { line: striker, isStriker: true, tag: "striker" },
            { line: nonStriker, isStriker: false, tag: "non-striker" },
          ].map(({ line, isStriker, tag }) => (
            <button
              key={line.playerId}
              className="ds-option"
              aria-pressed={outIsStriker === isStriker}
              onClick={() => setOutOverride(isStriker)}
            >
              <span className="ds-option__value" style={{ fontSize: "var(--ds-text-sm)" }}>{name(line.playerId)}</span>
              <span className="ds-option__label">{tag}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fielder */}
      {type === "caught_and_bowled" ? (
        <div>
          <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-1)" }}>Fielder</span>
          <div className="ds-caption">{name(bowlerId)} — the bowler took his own catch</div>
        </div>
      ) : needsFielder(type) ? (
        <div>
          <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-2)" }}>Fielder</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "var(--ds-space-2)", maxHeight: "25dvh", overflowY: "auto" }}>
            {fielders.map((player) => (
              <button
                key={player.id}
                className={`ds-key ds-key--sm${fielderId === player.id ? " ds-key--primary" : ""}`}
                style={{ height: 44, fontSize: "var(--ds-text-2xs)", fontFamily: "var(--ds-font-sans)" }}
                onClick={() => setFielderOverride(player.id)}
              >
                {player.id === keeperId ? "† " : ""}{player.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Runs completed */}
      <div>
        <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-2)" }}>Runs completed</span>
        <div className="row" style={{ gap: "var(--ds-space-3)", justifyContent: "center" }}>
          <button
            className="ds-key ds-key--sm"
            style={{ width: 48, height: 44, fontSize: "var(--ds-text-lg)" }}
            onClick={() => setRuns((n) => Math.max(0, n - 1))}
            aria-label="One fewer run"
          >
            −
          </button>
          <span style={{ fontSize: "var(--ds-text-2xl)", fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 40, textAlign: "center" }}>
            {runs}
          </span>
          <button
            className="ds-key ds-key--sm"
            style={{ width: 48, height: 44, fontSize: "var(--ds-text-lg)" }}
            onClick={() => setRuns((n) => n + 1)}
            aria-label="One more run"
          >
            +
          </button>
        </div>
      </div>

      {/* Dismissed at which end */}
      {asksEnd ? (
        <div>
          <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-2)" }}>Dismissed at</span>
          <div className="ds-options">
            {(["strikers", "non_strikers"] as const).map((option) => (
              <button
                key={option}
                className="ds-option"
                aria-pressed={end === option}
                onClick={() => setEndOverride(option)}
              >
                <span className="ds-option__value" style={{ fontSize: "var(--ds-text-sm)" }}>
                  {option === "strikers" ? "Striker's end" : "Non-striker's end"}
                </span>
              </button>
            ))}
          </div>
          <div className="ds-caption" style={{ marginTop: "var(--ds-space-1)" }}>Where the batter who is out was standing</div>
        </div>
      ) : null}

      {/* Did batters cross */}
      {asksCrossed ? (
        <div>
          <span className="ds-label" style={{ display: "block", marginBottom: "var(--ds-space-2)" }}>Did the batters cross?</span>
          <div className="ds-options">
            {[true, false].map((option) => (
              <button
                key={String(option)}
                className="ds-option"
                aria-pressed={crossed === option}
                onClick={() => setCrossed(option)}
              >
                <span className="ds-option__value">{option ? "Yes" : "No"}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Actions */}
      <div style={{ display: "flex", gap: "var(--ds-space-2)", marginTop: "var(--ds-space-1)" }}>
        <button className="ds-btn ds-btn--secondary" style={{ flex: "none" }} onClick={onCancel}>Cancel</button>
        <button className="ds-btn ds-btn--primary ds-btn--block" onClick={confirm} disabled={missingFielder}>
          {missingFielder ? "Pick a fielder" : `Out — ${name(playerOut.playerId)}`}
        </button>
      </div>
    </div>
  );
}
